/* eslint-disable indent */
import { useCallback } from 'react'

import { toast as sonnerToast } from 'sonner'

export interface Toast {
  title: string
  description?: string
  type?: 'success' | 'error' | 'warning' | 'info'
  duration?: number
  action?: {
    label: string
    onClick: () => void
  }
}

export interface ToastReturn {
  showToast: (toast: Toast) => void
  success: (message: string, description?: string) => void
  error: (message: string, description?: string) => void
  warning: (message: string, description?: string) => void
  info: (message: string, description?: string) => void
  promise: <T>(
    promise: Promise<T>,
    messages: {
      loading: string
      success: string | ((data: T) => string)
      error: string | ((error: Error) => string)
    }
  ) => void
  dismiss: (toastId?: string | number) => void
  dismissAll: () => void
}

export const useToast = (): ToastReturn => {
  const showToast = useCallback((toast: Toast) => {
    const { title, description, type = 'info', duration, action } = toast

    const options = {
      description,
      duration,
      action: action
        ? {
            label: action.label,
            onClick: action.onClick
          }
        : undefined
    }

    switch (type) {
      case 'success':
        sonnerToast.success(title, options)
        break
      case 'error':
        sonnerToast.error(title, options)
        break
      case 'warning':
        sonnerToast.warning(title, options)
        break
      case 'info':
      default:
        sonnerToast.info(title, options)
        break
    }
  }, [])

  const success = useCallback((message: string, description?: string) => {
    sonnerToast.success(message, { description })
  }, [])

  const error = useCallback((message: string, description?: string) => {
    sonnerToast.error(message, { description })
  }, [])

  const warning = useCallback((message: string, description?: string) => {
    sonnerToast.warning(message, { description })
  }, [])

  const info = useCallback((message: string, description?: string) => {
    sonnerToast.info(message, { description })
  }, [])

  const promise = useCallback(
    <T>(
      promise: Promise<T>,
      messages: {
        loading: string
        success: string | ((data: T) => string)
        error: string | ((error: Error) => string)
      }
    ) => {
      return sonnerToast.promise(promise, messages)
    },
    []
  )

  const dismiss = useCallback((toastId?: string | number) => {
    sonnerToast.dismiss(toastId)
  }, [])

  const dismissAll = useCallback(() => {
    sonnerToast.dismiss()
  }, [])

  return {
    showToast,
    success,
    error,
    warning,
    info,
    promise,
    dismiss,
    dismissAll
  }
}
